import React, { useEffect, useState, useRef, useMemo } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { NVLineChart } from "common/graphs/NVLineChart";
import HeatMap from "common/graphs/HeatMap/HeatMap";
import Legend from "common/graphs/Legend";
import { useDimensions } from "common/hooks";
import { threeSignificantDigitsFormat, unitToPrecisionMap } from "common/utils";
import { ColorLegendVertical } from "common/graphs/HeatMap/ColorLegendVertical";
import {
  tooltipContentGeneratorForUnits,
  getToppedValue,
} from "../../Subscribers/Report/TimeEvolutionMetrics/utils";
import { getDataValues } from "common/graphs/HeatMap/utils";
import { getMeanValue } from "common/api";
import BoxPlotSubscribers from "../../Subscribers/Report/TimeEvolutionMetrics/BoxPlotSubscribers";

const CHART_HEIGHT = 380;
const CHART_MARGIN = { top: 20, right: 40, bottom: 30, left: 240 };
const Y_AXIS_TO_GAP = 0;

const Wrapper = styled.div`
  width: 100%;
`;

const ChartContainer = styled.div`
  height: ${(props) => props.height + "px"};
  position: relative;

  svg.nvd3-svg .nv-axis path.domain {
    stroke-opacity: 0;
  }

  .nv-y.nv-axis .nv-axisMaxMin {
    display: none;
  }

  .nv-y.nv-axis .tick.zero line {
    stroke: transparent !important;
  }

  .nvd3 .nv-groups path.nv-line {
    stroke-width: 1.1;
  }
`;

const HeatMapContainer = styled.div`
  margin-top: 0px;
`;

const MetricHeader = styled.div`
  font-size: 13px !important;
  padding-top: 0px;
  width: 100%;
  text-align: center;
  font-family: "AllianceNo2-SemiBold" !important;
  padding: 0px 40px 0 240px;
  margin-bottom: -10px;
  color: var(--Text-Primary);
`;

const LegendContainer = styled.div`
  width: 100%;
  padding: 20px 0 0 240px;
`;

const ColorLegendContainer = styled.div`
  height: 330px;
  width: 40px;
  position: absolute;
  left: 100px;
  bottom: 30px;
  z-index: 2;
`;

const HighlightThing = styled.div`
  width: 12px;
  height: 12px;
  position: absolute;
  border-radius: 12px;
  background-color: ${(props) => props.color};
  left: ${(props) => props.xPos - 8 + "px"};
  top: ${(props) => props.yPos - 8 + "px"};
`;

const Charts = ({
  inRangeItems,
  yAxisUnits,
  fields,
  yTooltipFormat,
  xAxisFormat,
  onSeriesInspect,
  rangeY,
  tooltipUnits,
  sampledValues,
  heatmapFormat,
  metric,
  subsGroupsToPlot,
  meanSorting,
  setMeanSorting,
  setFieldsMeanMap,
}) => {
  const [newFields, setNewFields] = useState([]);
  const [legendFieldsSet, setLegendFieldsSet] = useState(new Set());
  const [colorScaleMax, setColorScaleMax] = useState();
  const [colorScaleMin, setColorScaleMin] = useState();
  const [hoveredCell, setHoveredCell] = useState(null);
  const [origValues, setOrigValues] = useState();
  const dataValues = getDataValues(inRangeItems, legendFieldsSet);
  const [min = 0, max = 0] = d3.extent(dataValues);
  const legendHeightWrapper =
    CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom;
  const legendHeight =
    legendHeightWrapper - legendHeightWrapper * Y_AXIS_TO_GAP;
  const yScaleLegend = d3
    .scaleLinear()
    .range([legendHeight, 0])
    .domain([0, max]);
  const yScaleLegendSpec = d3
    .scaleLinear()
    .range([legendHeight, 0])
    .domain([colorScaleMin || 0, colorScaleMax || max]);
  const maxMax = colorScaleMax || max;
  const topGap =
    yScaleLegend(maxMax) - yScaleLegend(maxMax + maxMax * Y_AXIS_TO_GAP);
  const topGapSpec =
    yScaleLegendSpec(maxMax) -
    yScaleLegendSpec(maxMax + maxMax * Y_AXIS_TO_GAP);
  const yScaleLegendInverse = d3
    .scaleLinear()
    .range([max, 0])
    .domain([0, legendHeight]);
  const { yColor, xPosHighLight, value: highlightValue } = hoveredCell || {};
  const yPosHightlight = highlightValue
    ? getToppedValue({
        value: Number(highlightValue),
        min: colorScaleMin,
        max: colorScaleMax,
        yScale: yScaleLegendSpec,
        height: legendHeight,
        topGap: topGapSpec,
        totalHeight: legendHeightWrapper,
      }) + CHART_MARGIN.top
    : null;
  const chartRef = useRef(null);
  const dimensions = useDimensions(chartRef);

  const orderSelectionMap = useMemo(() => {
    return subsGroupsToPlot?.reduce((acc, sub, index) => {
      const key = sub["SUBSCRIBER-GROUP"];
      acc[key] = index;
      return acc;
    }, {});
  }, [subsGroupsToPlot]);

  const subsGroupsToPlotMap = useMemo(() => {
    return subsGroupsToPlot?.reduce((acc, sub, index) => {
      const key = sub["SUBSCRIBER-GROUP"];
      acc[key] = sub;
      return acc;
    }, {});
  }, [subsGroupsToPlot]);

  const fieldNames = fields?.map((f) => f.name).join(",") || "";

  useEffect(() => {
    if (fields && fields.length > 0) {
      const fieldsSet = new Set(fields.map(({ name }) => name));
      setLegendFieldsSet(fieldsSet);
      setNewFields([...fields]);
    }
  }, [fieldNames]);

  const nItems = inRangeItems ? inRangeItems.length : 0;

  useEffect(() => {
    setColorScaleMax(undefined);
    setColorScaleMin(undefined);
    setOrigValues(undefined);
  }, [nItems, metric]);

  const handleMinMaxValue = (value, type) => {
    setOrigValues({
      yScale: yScaleLegend,
      yScaleInverse: yScaleLegendInverse,
      min,
      max,
    });
    if (type === "max") {
      setColorScaleMax(value);
    } else {
      setColorScaleMin(value);
    }
  };
  const maxScale = colorScaleMax !== undefined ? colorScaleMax : max;
  const minScale = colorScaleMin !== undefined ? colorScaleMin : min;

  const filteredSampledValues = useMemo(() => {
    if (!sampledValues) return {};
    return Object.entries(sampledValues).reduce((acc, [key, value]) => {
      acc[key] = value.map((v) => {
        if (v > maxScale) return maxScale;
        if (v < minScale) return minScale;
        return v;
      });
      return acc;
    }, {});
  }, [sampledValues, maxScale, minScale]);

  const { fieldsMeanMap, fieldsWithoutValues, fieldsWithValues } =
    useMemo(() => {
      return newFields.reduce(
        (acc, field) => {
          const { name } = field;
          if (filteredSampledValues[name] !== undefined) {
            acc.fieldsMeanMap[name] = getMeanValue(filteredSampledValues[name]);
            acc.fieldsWithValues.push(field);
          } else {
            acc.fieldsWithoutValues.push(field);
          }
          return acc;
        },
        { fieldsMeanMap: {}, fieldsWithoutValues: [], fieldsWithValues: [] }
      );
    }, [newFields, filteredSampledValues]);

  useEffect(() => {
    if (setFieldsMeanMap) {
      setFieldsMeanMap((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(fieldsMeanMap)) return prev;
        return fieldsMeanMap;
      });
    }
  }, [fieldsMeanMap, setFieldsMeanMap]);

  const orderedFields = useMemo(() => {
    if (meanSorting !== "default") {
      const sortedFields = [...fieldsWithValues].sort((a, b) => {
        if (meanSorting === "asc") {
          return fieldsMeanMap[a.name] - fieldsMeanMap[b.name];
        } else {
          return fieldsMeanMap[b.name] - fieldsMeanMap[a.name];
        }
      });
      return [...sortedFields, ...fieldsWithoutValues];
    } else {
      return [...newFields].sort((a, b) => {
        const aIndex = orderSelectionMap[a.name];
        const bIndex = orderSelectionMap[b.name];
        return aIndex - bIndex;
      });
    }
  }, [
    newFields,
    meanSorting,
    fieldsWithValues,
    fieldsWithoutValues,
    fieldsMeanMap,
    orderSelectionMap,
  ]);

  return (
    <Wrapper ref={chartRef}>
      <MetricHeader>{`${metric.toUpperCase()} (${yAxisUnits})`} </MetricHeader>
      <LegendContainer>
        <Legend
          fields={orderedFields}
          value={legendFieldsSet}
          onChange={(filterSet) => {
            setLegendFieldsSet(filterSet);
          }}
          onSeriesInspect={onSeriesInspect}
          itemWidth={120}
          margin={{ top: 0, right: CHART_MARGIN.right, bottom: 0, left: 0 }}
        />
      </LegendContainer>
      <ChartContainer height={CHART_HEIGHT}>
        {fields && fields.length > 0 ? (
          <ColorLegendContainer>
            <ColorLegendVertical
              width={10}
              height={legendHeight}
              min={origValues ? origValues.min : min}
              max={origValues ? origValues.max : max}
              yScale={origValues ? origValues.yScale : yScaleLegend}
              yScaleInverse={
                origValues ? origValues.yScaleInverse : yScaleLegendInverse
              }
              onMinChanged={(value) => handleMinMaxValue(value, "min")}
              onMaxChanged={(value) => handleMinMaxValue(value, "max")}
              precision={unitToPrecisionMap[yAxisUnits]}
              yAxisTopGap={Y_AXIS_TO_GAP}
              colorScaleMaxOverride={colorScaleMax}
              colorScaleMinOverride={colorScaleMin}
              tickFormat={(value) =>
                threeSignificantDigitsFormat(value, yAxisUnits)
              }
              interactionData={hoveredCell}
            />
          </ColorLegendContainer>
        ) : null}
        {hoveredCell !== null && hoveredCell.value !== undefined ? (
          <HighlightThing
            color={yColor}
            xPos={xPosHighLight}
            yPos={yPosHightlight}
          />
        ) : null}
        <NVLineChart
          showLegend={false}
          xField="time"
          items={inRangeItems}
          yAxisUnits={yAxisUnits}
          xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
          yAxisFormat={(value) =>
            threeSignificantDigitsFormat(value, yAxisUnits)
          }
          noDataMessage="No groups selected"
          fields={orderedFields.filter(({ name }) => legendFieldsSet.has(name))}
          yAxisTopGap={Y_AXIS_TO_GAP}
          yAxisTicksNumber={5}
          yTooltipFormat={yTooltipFormat}
          onSeriesInspect={onSeriesInspect}
          margin={CHART_MARGIN}
          yMaxValue={colorScaleMax}
          yMinValue={colorScaleMin}
          tooltipContentGenerator={tooltipContentGeneratorForUnits(
            yAxisUnits,
            yTooltipFormat
          )}
        />
      </ChartContainer>
      <HeatMapContainer>
        <HeatMap
          margin={{
            top: 0,
            right: CHART_MARGIN.right,
            bottom: 15,
            left: CHART_MARGIN.left,
          }}
          fields={orderedFields}
          legendSet={legendFieldsSet}
          items={inRangeItems}
          unit={tooltipUnits}
          setLegendFieldsSet={setLegendFieldsSet}
          onSeriesInspect={onSeriesInspect}
          precision={unitToPrecisionMap[yAxisUnits]}
          onMinChanged={(value) => setColorScaleMin(value)}
          onMaxChanged={(value) => setColorScaleMax(value)}
          colorScaleMaxOverride={colorScaleMax}
          colorScaleMinOverride={colorScaleMin}
          hideColorLegend={true}
          tickFormat={(value) =>
            threeSignificantDigitsFormat(value, yAxisUnits)
          }
          sampledValues={sampledValues}
          heatmapFormat={heatmapFormat}
          metric={metric}
          setHoveredCell={(cell) => {
            setHoveredCell({
            ...cell,
            ...(cell?.yLabel
              ? { sub: subsGroupsToPlotMap[cell.yLabel] }
              : {}),
            entity: "subscriber-group",
            });
          }}
          hoveredCell={hoveredCell}
          subscribersToPlot={subsGroupsToPlot}
          width={dimensions.width}
          fieldsMeanMap={fieldsMeanMap}
          meanSorting={meanSorting}
          onMeanSortingChange={setMeanSorting}
        />
      </HeatMapContainer>
      <BoxPlotSubscribers
        items={inRangeItems}
        fields={orderedFields}
        fieldsSet={legendFieldsSet}
        setSelection={setLegendFieldsSet}
        selection={legendFieldsSet}
        onContextClick={onSeriesInspect}
        unit={tooltipUnits}
        filterMax={colorScaleMax}
        filterMin={colorScaleMin}
        metric={metric}
      />
    </Wrapper>
  );
};

export default Charts;
